import moment from 'moment';

export const formatNameToCamelCase = (name: string): string => {
  if (!name) return '';

  return name
    .trim()
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export const isValidUrl = (value: string | null | undefined): boolean => {
  if (!value || typeof value !== 'string' || value.trim() === '') return false;

  // Check if it looks like a URL (contains domain pattern)
  // Requires: optional protocol + domain name (at least 1 char) + dot + TLD (at least 2 chars)
  const urlPattern =
    /^(https?:\/\/)?([a-z\d]([a-z\d-]*[a-z\d])?\.)+[a-z]{2,}(\/[^\s]*)?$/i;
  if (!urlPattern.test(value)) return false;

  try {
    // Add protocol if missing
    const urlToTest =
      value.startsWith('http://') || value.startsWith('https://')
        ? value
        : `https://${value}`;
    const url = new URL(urlToTest);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

/**
 * Converts a UTC date string to local time with the specified format
 * @param value - UTC date string
 * @param format - Moment.js format string (default: auto-detect)
 * @param fallback - Value to return if date is invalid (default: 'NA')
 * @param isDateOfBirth - If true, keeps date in UTC without timezone conversion (default: false)
 * @returns Formatted local date string or fallback value
 */
export const formatDate = (
  value?: string | null,
  format?: string,
  fallback = 'N/A',
  isDateOfBirth = false
): string => {
  if (!value) return fallback;

  const m = moment.utc(value);
  if (!m.isValid()) return fallback;

  // Check if the date has a time component (not midnight UTC)
  const hasTime = m.hours() !== 0 || m.minutes() !== 0 || m.seconds() !== 0;

  // Auto-detect format if not provided
  if (!format) {
    format = hasTime ? 'MM/DD/YYYY, HH:mm' : 'MM/DD/YYYY';
  }

  // Keep date-only values (midnight UTC) in UTC to prevent timezone shift
  // Only convert to local time if there's an actual time component
  return isDateOfBirth || !hasTime
    ? m.format(format)
    : m.local().format(format);
};

/**
 * Checks if a value is a valid ISO 8601 date string
 * @param value - Value to check
 * @returns true if value is a valid ISO 8601 date string
 */
export const isValidDate = (value: unknown): boolean => {
  if (!value || typeof value !== 'string') return false;

  // Reject pure numbers (e.g., "2123", "123")
  if (/^\d+$/.test(value.trim())) return false;

  // Must contain date separators (-, /, or T for ISO format)
  if (!/[-/T]/.test(value)) return false;

  // Strict ISO 8601 parsing
  return moment.utc(value, moment.ISO_8601, true).isValid();
};

/**
 * Formats a field value, automatically converting UTC dates to local time
 * @param value - Field value (can be date string or any other value)
 * @param options - Optional configuration
 * @param options.format - Date format to use if value is a date (default: auto-detect based on time component)
 * @param options.isDateOfBirth - If true, uses date-only format (MM/DD/YYYY) and keeps date in UTC without timezone conversion
 * @param options.fallback - Value to return if field is empty (default: 'NA')
 * @returns Formatted value
 */
export const formatFieldValue = (
  value?: string | null,
  options?: {
    format?: string;
    isDateOfBirth?: boolean;
    fallback?: string;
  }
): string => {
  const fallback = options?.fallback ?? 'N/A';

  if (!value) return fallback;

  // Check if it's a valid date
  if (isValidDate(value)) {
    // If isDateOfBirth is true, use date-only format without timezone conversion
    const format = options?.isDateOfBirth ? 'MM/DD/YYYY' : options?.format;
    return formatDate(value, format, fallback, options?.isDateOfBirth);
  }

  // Return the value as-is if it's not a date
  return value;
};
